import type { DeviceProfile } from "@talent/protocol";

/**
 * Device and shell detection.
 *
 * Everything here is COARSE on purpose. Behavioural telemetry about a named child is
 * already sensitive (v2 §32); a precise device fingerprint would add identifiability
 * for no measurement gain. Dimensions are rounded, DPR is truncated to one decimal,
 * and CPU/memory are bucketed rather than reported.
 */

/** Which delivery artifact are we running inside (ADR-002)? */
export function detectShell(): DeviceProfile["shell"] {
  // A TWA launches the origin from the Android app, which shows up as an
  // `android-app://` referrer. This is the standard signal and it is a heuristic, not
  // a guarantee — which is why the value is recorded as data rather than trusted.
  if (document.referrer.startsWith("android-app://")) return "twa";
  if ("Capacitor" in globalThis) return "capacitor";
  if (window.matchMedia("(display-mode: standalone)").matches) return "pwa";
  return "browser";
}

function detectEngine(): DeviceProfile["engine"] {
  const ua = navigator.userAgent;
  if (/Firefox\//.test(ua)) return "gecko";
  // Order matters: Chrome on iOS is WebKit underneath, and Chrome's UA contains
  // "Safari". Test for Blink markers first, then fall back to WebKit.
  if (/Chrome\/|Chromium\/|Edg\//.test(ua) && !/iPhone|iPad|iPod/.test(ua)) return "blink";
  if (/Safari\/|AppleWebKit\//.test(ua)) return "webkit";
  return "unknown";
}

function bucketCpu(): DeviceProfile["cpuClass"] {
  const n = navigator.hardwareConcurrency;
  if (!n) return "unknown";
  if (n <= 4) return "low";
  if (n <= 8) return "mid";
  return "high";
}

function bucketMemory(): DeviceProfile["memoryClass"] {
  const gb = (navigator as { deviceMemory?: number }).deviceMemory;
  if (gb === undefined) return "unknown";
  if (gb <= 2) return "low";
  if (gb <= 4) return "mid";
  return "high";
}

/**
 * Estimate refresh rate by sampling frame deltas.
 *
 * Informational only. PC-1 excludes reaction-time constructs precisely because device
 * timing cannot be trusted `[FACT: S8, S9, S10]`, so this value must never enter a
 * construct estimate. It exists to answer an engineering question — which device
 * classes hold their frame budget (ADR-002 exit criteria) — not a measurement one.
 */
export async function estimateRefreshRate(samples = 30): Promise<number | null> {
  if (typeof requestAnimationFrame !== "function") return null;
  const deltas: number[] = [];
  let last = performance.now();
  return new Promise((resolve) => {
    const tick = (now: number) => {
      deltas.push(now - last);
      last = now;
      if (deltas.length >= samples) {
        // Median, not mean: a single long frame during startup would skew a mean.
        const sorted = [...deltas].sort((a, b) => a - b);
        const mid = sorted[Math.floor(sorted.length / 2)];
        resolve(mid && mid > 0 ? Math.round(1000 / mid) : null);
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

export async function describeDevice(): Promise<DeviceProfile> {
  return {
    // Rounded to 10px to blunt fingerprinting while staying useful for layout analysis.
    viewportW: Math.round(window.innerWidth / 10) * 10,
    viewportH: Math.round(window.innerHeight / 10) * 10,
    devicePixelRatio: Math.round(window.devicePixelRatio * 10) / 10,
    refreshRateEstHz: await estimateRefreshRate(),
    cpuClass: bucketCpu(),
    memoryClass: bucketMemory(),
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    prefersReducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    shell: detectShell(),
    engine: detectEngine(),
  };
}

export const APP_VERSION = "0.1.0";
